import { Button, Group, NumberInput, Stack, Table, Text, ActionIcon } from "@mantine/core";
import { IconPlus, IconTrash } from "@tabler/icons-react";
import type { FeePolicyTier } from "../../apis/tenantFeePolicy";

interface Props {
  tiers: FeePolicyTier[];
  onChange: (next: FeePolicyTier[]) => void;
  readonly?: boolean;
}

// BIL-102: the UI caps tiers at 3 (the backend currently allows up to 10 — the
// stricter FE limit is intentional). Keep BIL-085 contiguity: first tier from 0,
// each tier's upper bound stitched to the next lower bound, last tier open.
const MAX_TIERS = 3;

// rate is stored as a decimal fraction (0.01 = 1%). The input shows it as a percent.
const rateToPercent = (rate?: string): number | undefined =>
  rate == null || rate === "" ? undefined : Number((Number(rate) * 100).toFixed(4));
const percentToRate = (pct: number | string): string =>
  pct === "" || pct == null ? "" : String(Number(pct) / 100);

export function TierEditor({ tiers, onChange, readonly = false }: Props) {
  const addTier = () => {
    if (tiers.length >= MAX_TIERS) return;
    const prevTo = tiers.length > 0 ? (tiers[tiers.length - 1].toAmount ?? "0") : "0";
    onChange([...tiers, { fromAmount: prevTo, toAmount: null, rate: "" }]);
  };

  const updateTier = (i: number, patch: Partial<FeePolicyTier>) => {
    const next = tiers.map((t, idx) => (idx === i ? { ...t, ...patch } : t));
    // Auto-stitch: editing a tier's upper bound syncs the next tier's lower bound.
    if (patch.toAmount !== undefined && i + 1 < next.length) {
      next[i + 1] = { ...next[i + 1], fromAmount: patch.toAmount ?? "0" };
    }
    onChange(next);
  };

  const removeTier = (i: number) => {
    onChange(tiers.filter((_, idx) => idx !== i));
  };

  return (
    <Stack gap="xs">
      <Group justify="space-between">
        <Text fw={600}>Түвшний тариф (max {MAX_TIERS})</Text>
        {!readonly && (
          <Button size="xs" leftSection={<IconPlus size={14} />} onClick={addTier} disabled={tiers.length >= MAX_TIERS}>
            Түвшин нэмэх
          </Button>
        )}
      </Group>
      <Table>
        <Table.Thead>
          <Table.Tr>
            <Table.Th>Дүн ≥</Table.Th>
            <Table.Th>Дүн &lt;</Table.Th>
            <Table.Th>Хувь</Table.Th>
            <Table.Th></Table.Th>
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {tiers.map((t, i) => (
            <Table.Tr key={i}>
              <Table.Td>
                {/* Derived: first tier always 0, the rest stitched from the previous toAmount. */}
                <NumberInput
                  value={t.fromAmount ? Number(t.fromAmount) : 0}
                  onChange={(v) => updateTier(i, { fromAmount: String(v ?? "0") })}
                  min={0} hideControls readOnly={readonly} disabled={!readonly}
                />
              </Table.Td>
              <Table.Td>
                <NumberInput
                  value={t.toAmount !== null ? Number(t.toAmount) : undefined}
                  placeholder={i === tiers.length - 1 ? "∞ (хязгааргүй)" : "Дараагийн түвшин"}
                  onChange={(v) => updateTier(i, { toAmount: v === "" || v === undefined ? null : String(v) })}
                  min={0} hideControls readOnly={readonly}
                />
              </Table.Td>
              <Table.Td>
                <NumberInput
                  value={rateToPercent(t.rate)}
                  onChange={(v) => updateTier(i, { rate: percentToRate(v as number | string) })}
                  min={0} max={99.99} step={0.01} decimalScale={2} suffix="%"
                  readOnly={readonly}
                />
              </Table.Td>
              <Table.Td>
                {!readonly && (
                  <ActionIcon variant="subtle" color="red" onClick={() => removeTier(i)} aria-label="Шатлал устгах">
                    <IconTrash size={16} />
                  </ActionIcon>
                )}
              </Table.Td>
            </Table.Tr>
          ))}
          {tiers.length === 0 && (
            <Table.Tr><Table.Td colSpan={4}><Text c="dimmed" ta="center">Түвшин нэмж эхэл.</Text></Table.Td></Table.Tr>
          )}
        </Table.Tbody>
      </Table>
    </Stack>
  );
}
